/**
 * Core CopyButton Component
 *
 * Click-to-copy with visual feedback (checkmark transition).
 * Used for IDs, API keys, connection strings, code snippets.
 *
 * @example
 *   <CopyButton value="abc-123-def" />
 *   <CopyButton value={connectionString} label="Copy Connection String" />
 */
import React, { useState, useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Copy, Check } from 'lucide-react';
import { Button } from '../primitives/Button';

export interface CopyButtonProps {
  /** Value to copy to clipboard */
  value: string;
  /** Show as button with label instead of icon-only */
  label?: string;
  /** Tooltip text */
  tooltip?: string;
  /** Size */
  size?: 'xs' | 'sm' | 'md';
  /** Variant — 'icon' for icon-only, 'button' for full button */
  variant?: 'icon' | 'button';
  /** Success callback */
  onCopy?: () => void;
  className?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  value,
  label,
  tooltip = 'Copy',
  size = 'sm',
  variant: buttonVariant = 'icon',
  onCopy,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [value, onCopy]);

  const iconSize = size === 'xs' ? 'h-3 w-3' : size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  if (buttonVariant === 'button' || label) {
    return (
      <Button
        variant="ghost"
        size={size}
        leftIcon={copied ? <Check className={iconSize} /> : <Copy className={iconSize} />}
        onClick={handleCopy}
        className={className}
      >
        {copied ? 'Copied!' : (label || 'Copy')}
      </Button>
    );
  }

  return (
    <Tooltip title={copied ? 'Copied!' : tooltip} arrow>
      <IconButton
        size="small"
        onClick={handleCopy}
        className={className}
        sx={{ color: copied ? 'success.main' : 'text.secondary' }}
      >
        {copied ? <Check className={iconSize} /> : <Copy className={iconSize} />}
      </IconButton>
    </Tooltip>
  );
};
export default CopyButton;
